import type { BannerAd, Vendor } from '@shopgnd/shared';
import { listActiveBannerAds } from '@/api/bannerAds';
import { getVendor } from '@/api/catalog';
import type { AdUnit, AdUnitPage } from '@/lib/adPlacements';
import { promoPlacementForPage } from '@/lib/adPlacements';
import { businessPath } from '@/lib/businessUrl';

export function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]![0] ?? ''}${words[1]![0] ?? ''}`.toUpperCase();
  }
  return name.slice(0, 3).toUpperCase();
}

function firstLine(text: string | undefined, max = 140): string {
  if (!text?.trim()) return '';
  const line = text.trim().split('\n')[0] ?? text;
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}

function ctaLabelFor(banner: BannerAd, vendor?: Vendor): string {
  if (vendor) return 'View profile';
  if (/\/advertise\b/i.test(banner.targetUrl)) return 'Advertise with us';
  if (/\/sell\b/i.test(banner.targetUrl)) return 'Start selling';
  return 'Learn more';
}

/** Map a live BannerAd (+ optional linked vendor) to the text promo card shape. */
export function bannerAdToAdUnit(
  banner: BannerAd,
  placement: AdUnit['placement'],
  vendor?: Vendor,
): AdUnit {
  const advertiserName = vendor?.businessName ?? banner.advertiserName;
  const headline = banner.altText.trim() || advertiserName;
  const subline =
    firstLine(vendor?.description) ||
    (vendor ? advertiserName : banner.advertiserName);
  const ctaHref = vendor ? businessPath(vendor) : banner.targetUrl;

  return {
    id: banner.id,
    advertiserId: banner.vendorId ?? banner.id,
    advertiserName,
    advertiserInitials: initialsFromName(advertiserName),
    logoBackground: vendor?.brandColor ?? '#005c34',
    logoColor: '#f2c300',
    headline,
    subline,
    ctaLabel: ctaLabelFor(banner, vendor),
    ctaHref,
    variant: 'gold',
    placement,
    pages: [],
  };
}

export async function loadPromoAd(
  page: AdUnitPage,
  kind: AdUnit['placement'],
): Promise<AdUnit | null> {
  const slot = promoPlacementForPage(page, kind);
  if (!slot) return null;

  const ads = await listActiveBannerAds([slot]);
  const banner = ads[0];
  if (!banner) return null;

  const vendor = banner.vendorId ? await getVendor(banner.vendorId) : undefined;
  return bannerAdToAdUnit(banner, kind, vendor);
}
